import {Injectable} from '@angular/core';
import {LineChartSettingsParameters} from '@models/charts/line-chart-settings-parameters';
import * as _Highcharts from 'highcharts';
import {Signal} from '@models/dashboard/signal';
import {ChartConstants} from '@constants/pages/chart-constants';
import {DatePipe, DecimalPipe} from '@angular/common';
import { TranslationService } from '@services/translation/translation.service';
import {ChartLegendSettingsGeneratorService} from '@services/charts/line/legend/chart-legend-settings-generator.service';

@Injectable({
  providedIn: 'root'
})
export class BarChartSettingsGeneratorService {

  constructor(private decimalPipe: DecimalPipe,
              private datePipe: DatePipe,
              private translationService: TranslationService,
              private chartLegendSettingsGeneratorService: ChartLegendSettingsGeneratorService) {
  }

  public generateChartOptions(chartParameters: LineChartSettingsParameters): _Highcharts.Options {
    const self = this;
    const series = this.generateSeries(chartParameters.chartSettings.signals, chartParameters.machineSignals, chartParameters.isTemplateWidget, chartParameters.machineId);
    const legend = this.chartLegendSettingsGeneratorService.generateLegendSettings(false, chartParameters.machineId, chartParameters.isInEditMode, chartParameters.chartId);
    legend.symbolPadding = 0;
    legend.symbolWidth = 0;
    legend.symbolHeight = 0;
    legend.squareSymbol = false;
    return {
      title: {
        text: undefined
      },
      chart: {
        type: 'column',
        events: {
          render: (event) => this.renderChart(event)

        }
      },
      tooltip: {
        backgroundColor: ChartConstants.TOOLTIP.BACKGROUND_COLOR,
        borderWidth: ChartConstants.TOOLTIP.BORDER_WIDTH,
        borderColor: ChartConstants.TOOLTIP.BORDER_COLOR,
        headerFormat: '',
        pointFormatter: function () {
          return self.tooltipFormatter(chartParameters.portalLanguage, this);
        }
      },
      yAxis: {
        title: {
          text: chartParameters.chartSettings.axisYLabel || '',
        },
        min: chartParameters.chartSettings.minY || null,
        max: chartParameters.chartSettings.maxY || null,
      },
      xAxis: {
        categories: series.filter(item => item.visible).map(item => item.name)
      },
      legend: legend,
      plotOptions: {
        column: {
          minPointLength: 1,
          grouping: false
        }
      },
      credits: {
        enabled: false,
        text: String(chartParameters.chartId)//!!!DO NOT REMOVE. needed to identify the chart by id
      },
      exporting: {
        enabled: false
      },
      series: series
    };

  }

  private generateSeries(signals: any[], machineSignals: Signal[], isTemplateWidget: boolean, machineId: string): any[] {
    let visibleSeriesIndex = 0;
    return signals.map((signal, index) => {
      let decimalPlaces = signal.precision || 1;
      let unit = signal.unit;
      let displayedText = signal.displayedText || signal.label || signal.signalName;
      let machineSignal: Signal | undefined;
      if (isTemplateWidget) {
        machineSignal = this.getMachineSignalByName(signal.signalName, machineSignals);
      } else {
        machineSignal = this.getMachineSignalById(signal.signalId, machineSignals);
      }
      if (machineSignal) {
        decimalPlaces = machineSignal.decimals;
        unit = machineSignal.unit;
        displayedText = machineSignal.label;
      }
      const id = String(signal.signalId ?? index);
      const signalName = signal.signalName || machineSignal?.name || id;
      const visible = this.chartLegendSettingsGeneratorService.getSignalVisibility(machineId, signalName);
      const x = visible ? visibleSeriesIndex++ : visibleSeriesIndex;
      return {
        type: 'column',
        showInLegend: true,
        id: id,
        name: this.translationService.translate.instant(displayedText),
        signalName: signalName,
        visible: visible,
        data: [{
          x: x,
          y: 0,
          unit: unit,
          precision: decimalPlaces
        }]
      };
    });
  }

  private renderChart(event) {
    if (!event || !event.target) {
      return;
    }
    const chart = event.target;

    chart.width = null;
    chart.height = '100%';
    chart.chartIsReady = true;
    this.synchronizeVisibleSeries(chart);
  }

  private synchronizeVisibleSeries(chart): void {
    const xAxis = chart.xAxis?.[0];
    if (!xAxis) {
      return;
    }
    const visibleSeries = chart.series.filter(item => item.visible);
    let chartChanged = false;
    visibleSeries.forEach((series, index) => {
      const point = series.data[0];
      if (point && point.x !== index) {
        point.update({x: index}, false);
        chartChanged = true;
      }
    });
    const categories = visibleSeries.map(item => item.name);
    const currentCategories = xAxis.categories || [];
    if (categories.length !== currentCategories.length || categories.some((category, index) => category !== currentCategories[index])) {
      xAxis.setCategories(categories, false);
      chartChanged = true;
    }
    if (chartChanged) {
      chart.redraw(false);
    }
  }

  private getMachineSignalById(id, machineSignals: Signal[]): Signal | undefined {
    return Object.values(machineSignals || []).find(function (item: Signal) {
      return item.id === id;
    });
  }

  private getMachineSignalByName(name: string, machineSignals: Signal[]): Signal | undefined {
    return Object.values(machineSignals || []).find(function (item: Signal) {
      return item.name.toLowerCase() === name.toLowerCase();
    });
  }

  private tooltipFormatter(language, tooltipControl): string {
    const pointDate = new Date(tooltipControl.options.ts);
    const value = this.decimalPipe.transform(tooltipControl.y, '1.' + tooltipControl.precision + '-' + tooltipControl.precision, this.translationService.getActiveLanguage());
    return '<span>' + this.datePipe.transform(pointDate, 'mediumTime', undefined, language) + '</span><br/>' +
        '<span style="color:' + tooltipControl.color + '">●</span> ' + tooltipControl.category + ': <b>' + value + ' ' + tooltipControl.unit + '</b><br/>';
  }
}
